import React from 'react';
import { useIntl } from 'react-intl';
import { MapPin, WifiHigh, Briefcase, Translate, Users, GraduationCap, Clock } from '@phosphor-icons/react';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { buildSrpFacetHref } from '../utils/buildSrpFacetHref';
import { useFormatMessage } from '../../../../../i18n-review/client/Msg';

const FACET_LINK_CLASS = 'underline decoration-current/30 underline-offset-2 hover:text-jc-accent';

export interface MetaGridProps {
  location?: string;
  /** When set, the location row gains a "see on map & check your commute" link to the SRP map. */
  mapCommuteHref?: string | null;
  remoteness?: string;
  field?: string;
  jobType?: string;
  normalisedRoleLevel?: string;
  requiredLanguages?: string[];
  peopleManagementTier?: string;
  educationLevel?: string;
  minimumYearsOfExperience?: number | null;
}

/**
 * 2-column icon+label grid (desktop) matching the redesign's meta block; collapses to a single
 * flowing text line on narrow widths via CSS. Only already-computed values are passed in — no
 * new data derivation, all fields already available/formatted in +Page.tsx.
 */
export function MetaGrid({
  location,
  mapCommuteHref,
  remoteness,
  field,
  jobType,
  normalisedRoleLevel,
  requiredLanguages,
  peopleManagementTier,
  educationLevel,
  minimumYearsOfExperience,
}: MetaGridProps): React.ReactElement | null {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const facetHref = (facetKey: string, value: string) => buildSrpFacetHref(intl.locale, facetKey, value);

  /** Renders one or more values in a row as individually clickable facet links, joined by a separator. */
  const facetParts = (parts: { facetKey: string; value: string }[], separator: string): React.ReactNode =>
    parts.map((part, i) => (
      <React.Fragment key={`${part.facetKey}-${part.value}`}>
        {i > 0 ? separator : null}
        <a href={facetHref(part.facetKey, part.value)} className={FACET_LINK_CLASS}>
          {fixMojibake(part.value)}
        </a>
      </React.Fragment>
    ));

  const rows: { key: string; icon: React.ReactElement; label: React.ReactNode; suffix?: React.ReactNode }[] = [];

  if (location) {
    rows.push({
      key: 'location',
      icon: <MapPin className="h-3.5 w-3.5" weight="duotone" />,
      label: <a href={facetHref('location', location)} className={FACET_LINK_CLASS}>{fixMojibake(location)}</a>,
      suffix: mapCommuteHref ? (
        <a href={mapCommuteHref} className="ml-1.5 text-jc-accent hover:underline whitespace-nowrap">
          {formatMessage({
            id: 'jobDetail.viewOnMapCommute',
            defaultMessage: 'See on map & check your commute',
            description: 'Link on the job detail page that opens the SRP map centered on this job to check the commute',
          })}
        </a>
      ) : undefined,
    });
  }
  if (remoteness || field) {
    const parts = [
      remoteness ? { facetKey: 'remoteness', value: remoteness } : null,
      field ? { facetKey: 'field', value: field } : null,
    ].filter((p): p is { facetKey: string; value: string } => p !== null);
    rows.push({
      key: 'remote-field',
      icon: <WifiHigh className="h-3.5 w-3.5" weight="duotone" />,
      label: facetParts(parts, ' · '),
    });
  }
  if (jobType || normalisedRoleLevel) {
    const parts = [
      jobType ? { facetKey: 'jobType', value: jobType } : null,
      normalisedRoleLevel ? { facetKey: 'normalisedRoleLevel', value: normalisedRoleLevel } : null,
    ].filter((p): p is { facetKey: string; value: string } => p !== null);
    rows.push({
      key: 'jobtype-level',
      icon: <Briefcase className="h-3.5 w-3.5" weight="duotone" />,
      label: facetParts(parts, ' · '),
    });
  }
  if (requiredLanguages && requiredLanguages.length > 0) {
    rows.push({
      key: 'languages',
      icon: <Translate className="h-3.5 w-3.5" weight="duotone" />,
      label: facetParts(requiredLanguages.map((lang) => ({ facetKey: 'requiredLanguages', value: lang })), ', '),
    });
  }
  if (peopleManagementTier) {
    rows.push({
      key: 'management',
      icon: <Users className="h-3.5 w-3.5" weight="duotone" />,
      label: <a href={facetHref('peopleManagementTier', peopleManagementTier)} className={FACET_LINK_CLASS}>{fixMojibake(peopleManagementTier)}</a>,
    });
  }
  if (educationLevel) {
    rows.push({
      key: 'education',
      icon: <GraduationCap className="h-3.5 w-3.5" weight="duotone" />,
      label: <a href={facetHref('educationLevel', educationLevel)} className={FACET_LINK_CLASS}>{fixMojibake(educationLevel)}</a>,
    });
  }
  if (minimumYearsOfExperience != null && minimumYearsOfExperience > 0) {
    rows.push({
      key: 'experience',
      icon: <Clock className="h-3.5 w-3.5" weight="duotone" />,
      label: formatMessage(
        { id: 'jobDetailPageV2.experienceYears', defaultMessage: '{years} {years, plural, one {year} other {years}} experience', description: 'Minimum years of experience, shown in the redesigned job detail meta grid' },
        { years: minimumYearsOfExperience },
      ),
    });
  }

  if (rows.length === 0) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
      {rows.map((row) => (
        <div key={row.key} className="flex items-center gap-1.5 text-[13px] font-medium text-[#4A4438] dark:text-[#C4A882]">
          <span className="flex-none text-[#8C8474]">{row.icon}</span>
          <span>
            {row.label}
            {row.suffix}
          </span>
        </div>
      ))}
    </div>
  );
}
